import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress
} from '@mui/material';
import axios from '../../utils/axios';

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [token, setToken] = useState('');
    const [userId, setUserId] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);

    useEffect(() => {
        // Extract token and userId from the URL
        const searchParams = new URLSearchParams(location.search);
        const tokenParam = searchParams.get('token');
        const userIdParam = searchParams.get('userId');

        if (tokenParam && userIdParam) {
            setToken(tokenParam);
            setUserId(userIdParam);
            validateToken(tokenParam, userIdParam);
        } else {
            setValidatingToken(false);
            setError('Invalid password reset link. Please request a new one.');
        }
    }, [location.search]);

    const validateToken = async (token, userId) => {
        try {
            await axios.get(`/api/users/validate-reset-token/${userId}/${token}`);
            setTokenValid(true);
            setValidatingToken(false);
        } catch (err) {
            setError('This password reset link is invalid or has expired. Please request a new one.');
            setValidatingToken(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(`/api/users/reset-password/${userId}/${token}`, { 
                password: formData.password 
            });
            
            setSuccess(response.data.message || 'Your password has been reset successfully. You can now login with your new password.');
            
            // Clear form
            setFormData({
                password: '',
                confirmPassword: ''
            });
            
            // Disable the form after successful reset
            setTokenValid(false);
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = () => {
        navigate('/login');
    };

    if (validatingToken) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Validating your reset link...
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Reset Password
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {!tokenValid && !success && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/forgot-password')}
                        >
                            Request New Reset Link
                        </Button>
                    </Box>
                )}

                {tokenValid && (
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="New Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            margin="normal"
                            required
                        />
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={loading}
                            >
                                {loading ? 'Resetting Password...' : 'Reset Password'}
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleLogin}
                                size="large"
                            >
                                Back to Login
                            </Button>
                        </Box>
                    </form>
                )}
            </Paper>
        </Container>
    );
};

export default ResetPassword; 